import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { pctIt } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Target, TrendingUp, Euro, CheckCircle2, X, Users, Filter, MessagesSquare } from "lucide-react";
import OneToOneModal from "@/components/OneToOneModal";

const DOT = { verde: "bg-emerald-500", giallo: "bg-amber-400", rosso: "bg-red-500" };
const euro = (n) => "€ " + (Number(n) || 0).toLocaleString("it-IT");

export default function HomeTeam() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [d, setD] = useState(null);
  const [agentId, setAgentId] = useState("");
  const [appro, setAppro] = useState(null);
  const [o2o, setO2o] = useState(null);

  const load = (aid = agentId) => api.get(`/dashboard/team${aid ? `?agent_id=${aid}` : ""}`).then((r) => setD(r.data)).catch(() => setD({}));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  if (!d) return <div className="animate-pulse h-64 rounded-2xl bg-slate-100" />;
  const im = d.imbuto || {};
  const funnel = [["Valutazioni", im.valutazioni], ["Incarichi", im.incarichi], ["Visite", im.visite], ["Offerte", im.offerte], ["Accettate", im.accettate]];
  const maxF = Math.max(1, ...funnel.map((f) => f[1] || 0));

  return (
    <div className="fade-up w-full max-w-full overflow-x-hidden" data-testid="home-team">
      <div className="mb-6">
        <div className="text-xs uppercase tracking-widest text-[var(--muted)] mb-1">{d.mese}</div>
        <h1 className="font-display text-3xl lg:text-4xl font-extrabold tracking-tight">{d.team_name}</h1>
        <p className="text-sm text-[var(--muted)] mt-1">Ciao {user?.name?.split(" ")[0]}, ecco come va il team.</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-5">
        <KPI icon={Target} label="Obiettivo incarichi (mese)" value={`${d.incarichi_mese} / ${d.obiettivo_mese || "—"}`} />
        <KPI icon={TrendingUp} label="Conversione valutazione→incarico" value={pctIt(im.pct_incarico || 0)} />
        <KPI icon={Euro} label="Provvigioni maturate team" value={euro(d.provvigioni_maturate)} />
      </div>

      {((d.da_fare?.prezzi_da_approvare || 0) + (d.da_fare?.sconti_da_approvare || 0) + (d.da_fare?.documenti_dubbi || 0)) > 0 && (
        <button data-testid="ht-da-approvare" onClick={() => setAppro(true)}
          className="w-full mb-5 flex items-center gap-3 p-4 rounded-2xl border-2 border-[#FC82ED]/50 bg-[#FC82ED]/10 text-left hover:-translate-y-0.5 transition-transform">
          <CheckCircle2 className="text-[#FC82ED] shrink-0" size={20} />
          <div className="text-sm flex-1"><b>{(d.da_fare?.prezzi_da_approvare || 0) + (d.da_fare?.sconti_da_approvare || 0) + (d.da_fare?.documenti_dubbi || 0)}</b> da approvare ({d.da_fare?.prezzi_da_approvare || 0} prezzi, {d.da_fare?.sconti_da_approvare || 0} sconti, {d.da_fare?.documenti_dubbi || 0} documenti dubbi)</div>
          <span className="text-xs font-semibold text-[#FC82ED]">Apri →</span>
        </button>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-5">
        <div className="card-immo p-5" data-testid="ht-semaforo">
          <div className="flex items-center gap-2 mb-3"><span className="w-8 h-8 rounded-xl bg-[#FC82ED]/15 text-[#FC82ED] flex items-center justify-center"><Users size={16} /></span><h2 className="font-display font-bold text-base">Il team oggi</h2></div>
          <div className="space-y-1.5">
            {(d.agenti || []).length === 0 ? <p className="text-sm text-[var(--muted)]">Nessun agente nel team.</p> :
              d.agenti.map((a) => (
                <div key={a.id} className="w-full flex items-center gap-3 p-2.5 rounded-xl hover:bg-slate-50 transition-colors">
                  <span className={`w-3 h-3 rounded-full shrink-0 ${DOT[a.colore]}`} data-testid={`ht-dot-${a.colore}`} />
                  <div className="min-w-0 flex-1" data-testid={`ht-agente-${a.id}`}>
                    <div className="text-sm font-medium truncate">{a.nome} <span className="text-xs text-[var(--muted)]">· {a.appuntamenti_oggi} oggi</span></div>
                    <div className="text-xs text-[var(--muted)] truncate">{a.motivo}</div>
                  </div>
                  <button data-testid={`ht-o2o-${a.id}`} onClick={() => setO2o(a.id)}
                    className="shrink-0 flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-[#111111] text-white text-xs font-semibold hover:bg-black"><MessagesSquare size={13} /> One-to-one</button>
                </div>
              ))}
          </div>
        </div>

        <div className="card-immo p-5" data-testid="ht-imbuto">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2"><span className="w-8 h-8 rounded-xl bg-[#FC82ED]/15 text-[#FC82ED] flex items-center justify-center"><Filter size={16} /></span><h2 className="font-display font-bold text-base">Imbuto del team</h2></div>
            <select data-testid="ht-imbuto-agente" value={agentId} onChange={(e) => { setAgentId(e.target.value); setD(null); load(e.target.value); }} className="text-xs px-2 py-1 rounded-lg border border-[var(--border)] max-w-[45%]">
              <option value="">Tutto il team</option>
              {(d.agenti_scelta || []).map((a) => <option key={a.id} value={a.id}>{a.nome}</option>)}
            </select>
          </div>
          <div className="space-y-2">
            {funnel.map(([label, v]) => (
              <div key={label}>
                <div className="flex justify-between text-sm mb-1"><span>{label}</span><b>{v || 0}</b></div>
                <div className="h-2 rounded-full bg-slate-100 overflow-hidden"><div className="h-full rounded-full bg-[#FC82ED]" style={{ width: `${Math.round(((v || 0) / maxF) * 100)}%` }} /></div>
              </div>
            ))}
          </div>
          {(d.motivi_non_presi || []).length > 0 && (
            <div className="mt-4 pt-3 border-t border-[var(--border)]">
              <div className="text-[11px] font-bold uppercase tracking-wider text-[var(--muted)] mb-2">Motivi non presi</div>
              <div className="flex flex-wrap gap-1.5">
                {d.motivi_non_presi.map((m) => <span key={m.motivo} className="text-xs px-2 py-1 rounded-full bg-slate-100">{m.motivo.replace(/_/g, " ")} · {m.n}</span>)}
              </div>
            </div>
          )}
          {(d.motivi_archiviazione || []).length > 0 && (
            <div className="mt-4 pt-3 border-t border-[var(--border)]" data-testid="ht-motivi-archiviazione">
              <div className="text-[11px] font-bold uppercase tracking-wider text-[var(--muted)] mb-2">Notizie archiviate (motivi del mese)</div>
              <div className="flex flex-wrap gap-1.5">
                {d.motivi_archiviazione.map((m) => <span key={m.motivo} className="text-xs px-2 py-1 rounded-full bg-amber-50 border border-amber-200">{m.label} · {m.n}</span>)}
              </div>
            </div>
          )}
        </div>
      </div>

      {appro && <ApprovazioniModal onClose={() => setAppro(null)} onDone={() => { setAppro(null); load(); }} />}
      {o2o && <OneToOneModal agentId={o2o} onClose={() => setO2o(null)} />}
    </div>
  );
}

const KPI = ({ icon: Icon, label, value }) => (
  <div className="card-immo p-4">
    <div className="flex items-center gap-2 text-[var(--muted)] mb-1"><Icon size={15} /><span className="text-xs">{label}</span></div>
    <div className="font-display text-xl font-extrabold">{value}</div>
  </div>
);

function ApprovazioniModal({ onClose, onDone }) {
  const [list, setList] = useState(null);
  const [sconti, setSconti] = useState([]);
  const [dubbi, setDubbi] = useState([]);
  const [busy, setBusy] = useState(null);
  const load = async () => {
    try { const r = await api.get("/team/approvazioni"); setList(r.data.prezzi || []); } catch { setList([]); }
    try { const s = await api.get("/sconti/approvazioni"); setSconti(s.data || []); } catch { setSconti([]); }
    try { const dd = await api.get("/documenti-dubbi"); setDubbi(dd.data || []); } catch { setDubbi([]); }
  };
  useEffect(() => { load(); }, []);
  const actPrezzo = async (id, approva) => {
    setBusy(id);
    try { await api.post(`/immobili/${id}/approva-prezzo`, { approva, nota: approva ? "" : "Rivedere il prezzo" }); toast.success(approva ? "Prezzo approvato" : "Prezzo rifiutato"); await load(); onDone(); }
    catch (e) { toast.error(e.response?.data?.detail || "Errore"); } finally { setBusy(null); }
  };
  const actSconto = async (id, approva) => {
    setBusy(id);
    try { await api.post(`/sconti/${id}/approva`, { approva }); toast.success(approva ? "Sconto approvato" : "Sconto rifiutato"); await load(); onDone(); }
    catch (e) { toast.error(e.response?.data?.detail || "Errore"); } finally { setBusy(null); }
  };
  return (
    <Overlay title="Da approvare" onClose={onClose} testid="approvazioni-modal">
      <div className="text-[11px] font-bold uppercase tracking-wider text-[var(--muted)] mb-2">Prezzi di pubblicazione</div>
      {!list ? <p className="text-sm text-[var(--muted)]">Carico…</p> : list.length === 0 ? <p className="text-sm text-[var(--muted)] mb-3">Nessun prezzo in attesa.</p> :
        list.map((p) => (
          <div key={p.id} data-testid={`appro-${p.id}`} className="p-3 rounded-xl border border-[var(--border)] mb-2">
            <div className="text-sm font-semibold">{p.titolo}</div>
            <div className="text-xs text-[var(--muted)] mb-2">{p.agente} · {p.manca_prezzo ? <b className="text-red-600" data-testid={`appro-manca-prezzo-${p.id}`}>Manca il prezzo</b> : <>proposto {euro(p.prezzo)}</>}</div>
            <div className="flex gap-2">
              <button data-testid={`appro-ok-${p.id}`} disabled={busy === p.id || p.manca_prezzo} onClick={() => actPrezzo(p.id, true)} className="flex-1 py-2 rounded-lg bg-emerald-500 text-white text-sm font-semibold disabled:opacity-50">Approva</button>
              <button data-testid={`appro-no-${p.id}`} disabled={busy === p.id} onClick={() => actPrezzo(p.id, false)} className="flex-1 py-2 rounded-lg bg-[#F7F7F8] border border-[var(--border)] text-sm font-medium disabled:opacity-50">Rifiuta</button>
            </div>
          </div>
        ))}
      <div className="text-[11px] font-bold uppercase tracking-wider text-[var(--muted)] mb-2 mt-4">Sconti provvigione</div>
      {sconti.length === 0 ? <p className="text-sm text-[var(--muted)]">Nessuno sconto in attesa.</p> :
        sconti.map((s) => (
          <div key={s.id} data-testid={`sconto-${s.id}`} className="p-3 rounded-xl border border-[var(--border)] mb-2">
            <div className="text-sm font-semibold">Sconto {pctIt(s.sconto_pct)}</div>
            <div className="text-xs text-[var(--muted)] mb-2">{s.richiedente} · {s.descrizione || "—"}</div>
            <div className="flex gap-2">
              <button data-testid={`sconto-ok-${s.id}`} disabled={busy === s.id} onClick={() => actSconto(s.id, true)} className="flex-1 py-2 rounded-lg bg-emerald-500 text-white text-sm font-semibold disabled:opacity-50">Approva</button>
              <button data-testid={`sconto-no-${s.id}`} disabled={busy === s.id} onClick={() => actSconto(s.id, false)} className="flex-1 py-2 rounded-lg bg-[#F7F7F8] border border-[var(--border)] text-sm font-medium disabled:opacity-50">Rifiuta</button>
            </div>
          </div>
        ))}
      <div className="text-[11px] font-bold uppercase tracking-wider text-[var(--muted)] mb-2 mt-4">Documenti dubbi dalla segreteria</div>
      {dubbi.length === 0 ? <p className="text-sm text-[var(--muted)]">Nessun documento dubbio.</p> :
        dubbi.map((x) => (
          <a key={`${x.immobile_id}-${x.doc}`} href={`/immobili/${x.immobile_id}?tab=documenti`} data-testid={`dubbio-row-${x.immobile_id}-${x.doc}`}
             className="block p-3 rounded-xl border border-violet-200 bg-violet-50/50 mb-2 hover:bg-violet-50">
            <div className="text-sm font-semibold">{x.titolo} · {x.doc_label}</div>
            <div className="text-xs text-[var(--muted)] break-words">{x.by}: {x.nota}</div>
            <div className="text-xs font-semibold text-violet-700 mt-1">Apri e decidi →</div>
          </a>
        ))}
    </Overlay>
  );
}


const Overlay = ({ title, children, onClose, testid }) => (
  <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4" data-testid={testid}>
    <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
    <div className="relative w-full sm:max-w-lg card-immo p-5 rounded-t-3xl sm:rounded-3xl max-h-[92vh] overflow-y-auto">
      <div className="flex items-center justify-between mb-3"><h3 className="font-display text-lg font-bold">{title}</h3><button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-100"><X size={18} /></button></div>
      {children}
    </div>
  </div>
);
